import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { 
  Shield, 
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export const SecurityPage = () => {
  const navigate = useNavigate();
  const { post } = useApi();
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [errors, setErrors] = useState({});

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!passwords.current) {
      newErrors.current = 'Mot de passe actuel requis';
    }
    
    if (!passwords.new) {
      newErrors.new = 'Nouveau mot de passe requis';
    } else if (passwords.new.length < 6) {
      newErrors.new = 'Minimum 6 caractères';
    }
    
    if (passwords.new !== passwords.confirm) {
      newErrors.confirm = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    setSaving(true);
    try {
      const result = await post('/auth/change-password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      
      if (result.success) {
        toast.success('Mot de passe modifié avec succès');
        navigate('/profile');
      } else {
        toast.error(result.error || 'Mot de passe actuel incorrect');
      }
    } catch (err) {
      toast.error('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper" data-testid="security-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="text-slate-400" size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Shield className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Sécurité</h1>
              <p className="text-slate-400 text-sm">Modifier le mot de passe</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
          {/* Current Password */}
          <div>
            <label className="label-uppercase">Mot de passe actuel</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                placeholder="Entrez votre mot de passe actuel"
                className={`pl-10 pr-12 bg-slate-950 border-slate-800 text-white h-12 ${errors.current ? 'border-red-500' : ''}`}
                data-testid="current-password"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.current && <p className="text-red-500 text-sm mt-1">{errors.current}</p>}
          </div>

          {/* New Password */}
          <div>
            <label className="label-uppercase">Nouveau mot de passe</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.new}
                onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                placeholder="Minimum 6 caractères"
                className={`pl-10 pr-12 bg-slate-950 border-slate-800 text-white h-12 ${errors.new ? 'border-red-500' : ''}`}
                data-testid="new-password"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.new && <p className="text-red-500 text-sm mt-1">{errors.new}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="label-uppercase">Confirmer le nouveau mot de passe</label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                placeholder="Répétez le nouveau mot de passe"
                className={`pl-10 pr-12 bg-slate-950 border-slate-800 text-white h-12 ${errors.confirm ? 'border-red-500' : ''}`}
                data-testid="confirm-password"
              />
              <button
                type="button"
                onClick={() => toggleShowPassword('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirm && <p className="text-red-500 text-sm mt-1">{errors.confirm}</p>}
          </div>
        </div>

        <Button
          onClick={handleSave}
          className="w-full h-14 btn-primary-glow"
          disabled={saving}
          data-testid="save-password"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Modification...
            </span>
          ) : (
            <>
              <Save size={20} className="mr-2" />
              Modifier le mot de passe
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SecurityPage;
